import { useRouter } from 'next/router';
import { useState } from 'react';
import styled from 'styled-components';
import i18n, { SettingsType } from 'utils/i18n';
import Button from 'components/button';
import AssessmentQuiz from 'components-new/assessment-quiz';
import { useForm, useFieldArray } from 'react-hook-form';

type AssessmentQuizFormProps = {
  quiz: any;
  submitting: boolean;
  stepNum?: number;
  totalSteps: number;
  onComplete: any;
};

interface StyledQuizProps {
  currentPage: number;
}

const AssessmentQuizForm: React.FC<AssessmentQuizFormProps> = ({ quiz = [], onComplete, submitting, totalSteps }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const [currentPage, setCurrentPage] = useState(1);
  const [answers, setAnswers] = useState([]);

  const onAnswerChange = (answerData: any) => {
    setAnswers((prevAnswers) => {
      // Remove the old answer data for this question if it exists
      const filteredAnswers = prevAnswers.filter(
        (answer) => answer['content-question-id'] !== answerData['content-question-id']
      );
      // Add the new answer data
      return [...filteredAnswers, answerData];
    });
  };

  const generateDefaultValues = (pages) => {
    return {
      pages: pages.map((page) => ({
        questions: page.questionPoolsCollection.items
          .reduce((acc, pool) => acc.concat(pool.questionsCollection.items), [])
          .filter(
            (question) =>
              question.questionFields.type === 'match' ||
              question.questionFields.type === 'single' ||
              question.questionFields.type === 'multi' ||
              question.questionFields.type === 'fill' ||
              question.questionFields.type === 'order'
          )
          .map((question) => {
            switch (question.questionFields.type) {
              case 'match':
                return {
                  answers: question.questionFields.answers.map((answer) => ({
                    answer: answer.initialOrder,
                  })),
                };
              case 'order':
                return {
                  answers: question.questionFields.answers.map((answer) => ({
                    answer: answer.initialOrder,
                  })),
                };
              case 'multi':
              case 'fill':
                return {
                  answers: [],
                };
              default:
                return {
                  answers: null,
                };
            }
          }),
      })),
    };
  };

  const defaultValues = generateDefaultValues(quiz.assessmentPagesCollection.items);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    clearErrors,
    register,
    trigger,
  } = useForm({
    defaultValues: defaultValues,
  });

  const { fields: pagesFields } = useFieldArray({
    control,
    name: 'pages',
  });

  const watchAllFields = watch();

  const onSubmit = async (data: any) => {
    // onComplete(selected);
    clearErrors();

    const extractPageAndQuestion = (name) => {
      const match = name.match(/pages\[(\d+)\].questions\[(\d+)\]/);
      return match ? { page: parseInt(match[1], 10), question: parseInt(match[2], 10) } : { page: 0, question: 0 };
    };

    const sortedData = answers.sort((a, b) => {
      const aNumbers = extractPageAndQuestion(a.name);
      const bNumbers = extractPageAndQuestion(b.name);

      if (aNumbers.page !== bNumbers.page) {
        return aNumbers.page - bNumbers.page;
      }

      return aNumbers.question - bNumbers.question;
    });

    // Calculating counts
    const correctAnswersCount = sortedData.filter((question) => question.correct).length;
    const wrongAnswersCount = sortedData.length - correctAnswersCount;
    const totalAnswersCount = sortedData.length;

    // answersForPage: [{
    //   'content-id': string,
    //   'content-question-id': string,
    //   'content-answer-id': string,
    // }],
    // correctAnswersCount: number,
    // wrongAnswersCount: number,
    // totalAnswersCount: number

    function processEntries(data) {
      const processedData = [];

      data.forEach((item: any) => {
        if (item['content-answer-id'].includes(',')) {
          const ids = item['content-answer-id'].split(',');

          ids.forEach((id) => {
            processedData.push({
              ...item,
              'content-answer-id': id,
            });
          });
        } else {
          processedData.push(item);
        }
      });

      return processedData;
    }

    const answersForPage = processEntries(sortedData).map((question) => ({
      'content-id': question['content-answer-id'],
      'content-question-id': question['content-question-id'],
      'content-pool-id': question['content-pool-id'],
      passed: question.correct,
    }));

    console.log('submit', {
      processEntries: processEntries(sortedData),
      sortedData,
      answersForPage,
      correctAnswersCount,
      wrongAnswersCount,
      totalAnswersCount,
    });

    onComplete(answersForPage, correctAnswersCount, wrongAnswersCount, totalAnswersCount);
  };

  const handleNextPage = async () => {
    //@ts-ignore
    const isValid = await trigger(`pages[${currentPage - 1}].questions`);
    if (isValid) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 0);
    } else {
      console.log('Validation failed for current page');
    }
  };

  const handlePrevPage = async () => {
    setCurrentPage(currentPage - 1);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 0);
  };

  return (
    <StyledAssessmentQuizForm>
      <form onSubmit={handleSubmit((data: any) => onSubmit(data))}>
        <div className="questions">
          {/* <AssessmentQuiz /> */}
          <StyledQuiz currentPage={currentPage}>
            {pagesFields.map((pageField, index) => (
              <div key={pageField.id} data-page={index + 1}>
                {totalSteps > 1 && (
                  <div className="page-info">
                    {currentPage} {dictionary.of} {totalSteps} {dictionary.pages}
                  </div>
                )}
                <AssessmentQuiz
                  questionPools={quiz.assessmentPagesCollection.items[index].questionPoolsCollection.items}
                  control={control}
                  register={register}
                  pageIndex={index}
                  onAnswerChange={onAnswerChange}
                />
              </div>
            ))}
          </StyledQuiz>
        </div>
        <div className="buttons">
          {totalSteps > 1 && (
            <Button mode="secondary" onClick={handlePrevPage} disabled={currentPage === 1}>
              {dictionary.assessmentBack}
            </Button>
          )}
          {currentPage !== totalSteps && (
            <Button mode="primary" onClick={handleNextPage}>
              {dictionary.assessmentNext}
            </Button>
          )}
          {currentPage === totalSteps && (
            <Button mode="primary" htmlType="submit" loading={submitting}>
              {dictionary.quizCheckAnswers}
            </Button>
          )}
        </div>
        {errors && errors?.pages?.[currentPage - 1] && (
          <div className="validation-text">{dictionary.quizValidationAllFieldsAreRequired}</div>
        )}
      </form>
    </StyledAssessmentQuizForm>
  );
};

export const StyledQuiz = styled.ul<StyledQuizProps>`
  & > div {
    display: none;
  }

  & > div[data-page='${(props) => props.currentPage}'] {
    display: block;
  }
`;

export const StyledAssessmentQuizForm = styled.ul`
  .buttons {
    margin: 1rem 0;
  }

  .validation-text {
    margin-top: 0.5rem;
    color: var(--danger-base);
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

export default AssessmentQuizForm;
